import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaBars,
  FaChevronDown,
} from "react-icons/fa";
import {
  User,
  LogOut,
  Download,
  Pill,
  FlaskConical,
  Activity,
  Package,
} from "lucide-react";

import SearchBar from "./SearchBar";
import SignInButton from "./SignInButton";
import LocationPopup from "../common/LocationPopup";
import LogoutPopup from "../../component/common/LogoutPopup";
import MobileSidebar from "./MobileSidebar";

import logo from "../assets/images/medrpha_logo.png";
import cartIcon from "../assets/images/add to cart.png";

import useNavbarAPI from "../../services/Navbar/NavbarAPI";

export default function Navbar({ onCartClick }) {
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const {
    user,
    totalItems,
    address,
    loading,
    fetchLocation,
    displayName,
    openCart,
    confirmLogout,
  } = useNavbarAPI();

  const handleLogoutClick = () => {
    setShowLogoutPopup(true);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setShowProfileDropdown(false);
  };

  // Click outside handler
  React.useEffect(() => {
    const handleClickOutside = () => {
      setShowProfileDropdown(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const navItems = [
    { name: "Medicine", route: "/medicine", icon: <Pill size={18} /> },
    { name: "Lab Tests", route: "/labtest", icon: <FlaskConical size={18} /> },
    { name: "Lab", route: "/lab", from: "lab", icon: <Activity size={18} /> },
    { name: "Packages", route: "/lab", from: "packages", icon: <Package size={18} /> },
  ];

  return (
    <>
      <nav className="fixed top-0 w-full bg-white shadow-sm z-50">
        <div className="h-16 md:h-[72px] px-4 md:px-6 lg:px-8 flex items-center justify-between gap-4">
          
          {/* LEFT SECTION */}
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                setShowMobileMenu(true);
              }}
            >
              <FaBars size={18} />
            </button>

            <Link to="/" className="flex-shrink-0">
              <img src={logo} alt="logo" className="h-8 md:h-10 object-contain" />
            </Link>
          </div>

          {/* SEARCH BAR - DESKTOP */}
          <div className="hidden lg:block flex-1 max-w-2xl mx-6">
            <SearchBar />
          </div>

          {/* RIGHT SECTION */}
          <div className="flex items-center gap-4">
            
            {/* LOCATION PICKER */}
            <div className="hidden md:flex items-center gap-2 text-gray-600 cursor-pointer group">
              <FaMapMarkerAlt className="text-red-500 text-base flex-shrink-0" />
            <div
              onClick={fetchLocation}
                className="flex items-center gap-1"
            >
                <span className="text-sm font-medium truncate max-w-[120px] lg:max-w-[180px]">
                  {loading ? "Detecting..." : address || "Select Location"}
              </span>
              <FaChevronDown
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenPopup(true);
                }}
                  className="text-gray-400 text-xs cursor-pointer hover:text-gray-600 transition-colors"
              />
              </div>
            </div>

            {/* DOWNLOAD APP BUTTON */}
            <button className="hidden xl:flex items-center gap-2 px-4 py-2 rounded-full border border-red-500 text-red-500 text-sm font-medium hover:bg-red-50 transition-all duration-200">
              <Download size={16} />
              <span>Download App</span>
            </button>

            {/* CART ICON */}
            <div
              className="relative cursor-pointer p-2 hover:bg-gray-50 rounded-full transition-colors duration-200"
              onClick={(e) => {
                e.stopPropagation();
                openCart();
              }}
            >
              <img src={cartIcon} alt="cart" className="w-5 h-5 object-contain" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full font-semibold">
                  {totalItems > 9 ? '9+' : totalItems}
                </span>
              )}
            </div>

            {/* USER PROFILE */}
            {user ? (
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setShowProfileDropdown((prev) => !prev)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-500 text-red-500 hover:bg-red-50 transition-all duration-200"
                >
                  <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center text-xs font-bold text-red-600 flex-shrink-0">
                    {displayName?.charAt(0)}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline-block max-w-[100px] truncate">
                    {displayName}
                  </span>
                </button>

                {showProfileDropdown && (
                  <div className="absolute right-0 mt-3 w-56 bg-white shadow-xl rounded-2xl py-2 z-50 border border-gray-100">
                    <div className="px-4 pb-3 mb-2 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-500 font-bold text-base">
                          {displayName?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm text-gray-800 truncate">
                            {displayName}
                          </h4>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleNavigate("/profile")}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <User size={16} className="text-gray-400" />
                      <span>My Profile</span>
                    </button>

                    <div className="px-4 pt-2 pb-1">
                      <button
                        onClick={handleLogoutClick}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-all duration-200"
                      >
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="scale-[0.80] sm:scale-[0.90] md:scale-100">
                <SignInButton />
              </div>
            )}
          </div>
        </div>

        {/* MOBILE SEARCH BAR */}
        <div className="lg:hidden px-4 pb-3 bg-white border-t border-gray-100">
          <SearchBar />
        </div>
      </nav>

      {/* DESKTOP SECONDARY NAVIGATION */}
      <div className="hidden lg:flex fixed top-[72px] w-full bg-white border-b border-gray-100 h-14 items-center justify-center z-40 shadow-sm">
        <div className="flex gap-2">
          {navItems.map((item, index) => {
            const isActive =
              location.pathname === item.route &&
              (!item.from || location.state?.from === item.from);
            return (
              <button
                key={index}
                onClick={() =>
                  navigate(item.route, {
                    state: { from: item.from, name: item.name },
                  })
                }
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-red-50 text-red-500"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                {item.icon}
                <span>{item.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* MOBILE SIDEBAR */}
      <MobileSidebar
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
        navItems={navItems}
        location={location}
        navigate={navigate}
        user={user}
        displayName={displayName}
        handleLogoutClick={handleLogoutClick}
        logo={logo}
      />

      {/* POPUPS */}
      <LogoutPopup
        isOpen={showLogoutPopup}
        onCancel={() => setShowLogoutPopup(false)}
        onConfirm={confirmLogout}
      />
      <LocationPopup isOpen={openPopup} onClose={() => setOpenPopup(false)} />
    </>
  );
}